import { useState, useRef, useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import { default as Modal } from '@atlaskit/modal-dialog/modal-dialog';
import Image from '@atlaskit/image';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import { RovoIcon } from '@atlaskit/logo';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge/lozenge';

import GenerateBriefingGif from '../../assets/GenerateBriefing.gif';
import CreateWithRovoGif from '../../assets/CreateWithRovo.gif';
import WhiteboardFlowGif from '../../assets/WhiteboardFlow.gif';
import { type JSX } from 'react/jsx-runtime';

const i18n = defineMessages({
	carouselModalTitle: {
		id: 'confluence-home-rovo-awareness.carousel-modal.title',
		defaultMessage: 'More ways to Rovo',
		description: 'Title for the Rovo Awareness modal',
	},
	carouselModalDescription: {
		id: 'confluence-home-rovo-awareness.carousel-modal.description',
		defaultMessage: 'Discover how you can use Rovo to make your life easier.',
		description: 'Description text beneath the title for the Rovo Awareness modal',
	},
	carouselModalAudioTitle: {
		id: 'confluence-home-rovo-awareness.carousel-modal.audio-title',
		defaultMessage: 'Get briefed on the go',
		description:
			'Title for carousel slide for Rovo audio briefings feature providing summaries in a portable format',
	},
	carouselModalAudioDescription: {
		id: 'confluence-home-rovo-awareness.carousel-modal.audio-description',
		defaultMessage:
			'Rovo′s audio briefings can fill you in on key information from your team.Just hit play and go for a walk.',
		description:
			'Description text for carousel slide for Rovo audio briefings feature providing summaries in a portable format',
	},
	carouselModalCreateTitle: {
		id: 'confluence-home-rovo-awareness.carousel-modal.create-title',
		defaultMessage: 'Bye-bye blank page',
		description:
			'Title for carousel slide for Rovo AI First Create feature not creating an empty page',
	},
	carouselModalCreateDescription: {
		id: 'confluence-home-rovo-awareness.carousel-modal.create-description',
		defaultMessage:
			'You describe, Rovo creates. Pages, live docs, whiteboards, databases — Rovo does it all.',
		description:
			'Description text for carousel slide for Rovo AI First Create feature with examples of content types',
	},
	carouselModalDiagramTitle: {
		id: 'confluence-home-rovo-awareness.carousel-modal.diagram-title',
		defaultMessage: 'Let flow charts flow',
		description: 'Title for carousel slide for Rovo whiteboards feature creating flows/flowcharts',
	},
	carouselModalDiagramDescription: {
		id: 'confluence-home-rovo-awareness.carousel-modal.diagram-description',
		defaultMessage: 'Rovo can draw the diagrams for you that clearly communicate your ideas.',
		description:
			'Description text for carousel slide for Rovo whiteboards feature to create flowcharts, diagrams, and other ways to communicate your ideas',
	},
	carouselModalCarouselImageAlt: {
		id: 'confluence-home-rovo-awareness.carousel-modal.carousel-image-alt',
		defaultMessage: 'New Feature',
		description: 'Alt text for carousel images. Currently temporary placeholder',
	},
	carouselModalPreviousLabel: {
		id: 'confluence-home-rovo-awareness.carousel-modal.previous-label',
		defaultMessage: 'Previous',
		description: 'Label for the previous button to navigate to the previous slide',
	},
	carouselModalNextLabel: {
		id: 'confluence-home-rovo-awareness.carousel-modal.next-label',
		defaultMessage: 'Next',
		description: 'Label for the next button to navigate to the next slide',
	},
	carouselModalLozengeNew: {
		id: 'confluence-home-rovo-awareness.carousel-modal.lozenge-new',
		defaultMessage: 'New',
		description: 'Lozenge text indicating a new feature',
	},
	carouselModalLozengeComingSoon: {
		id: 'confluence-home-rovo-awareness.carousel-modal.lozenge-coming-soon',
		defaultMessage: 'Coming soon',
		description: 'Lozenge text indicating a feature that is coming soon',
	},
});

type CarouselModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const getCarouselSlides = (formatMessage: IntlShape['formatMessage']) => [
	{
		title: formatMessage(i18n.carouselModalAudioTitle),
		description: formatMessage(i18n.carouselModalAudioDescription),
		imageAlt: formatMessage(i18n.carouselModalCarouselImageAlt),
		imageSrc: GenerateBriefingGif,
		lozenge: { text: formatMessage(i18n.carouselModalLozengeNew), appearance: 'new' as const },
	},
	{
		title: formatMessage(i18n.carouselModalCreateTitle),
		description: formatMessage(i18n.carouselModalCreateDescription),
		imageAlt: formatMessage(i18n.carouselModalCarouselImageAlt),
		imageSrc: CreateWithRovoGif,
		lozenge: {
			text: formatMessage(i18n.carouselModalLozengeComingSoon),
			appearance: 'inprogress' as const,
		},
	},
	{
		title: formatMessage(i18n.carouselModalDiagramTitle),
		description: formatMessage(i18n.carouselModalDiagramDescription),
		imageAlt: formatMessage(i18n.carouselModalCarouselImageAlt),
		imageSrc: WhiteboardFlowGif,
		lozenge: { text: formatMessage(i18n.carouselModalLozengeNew), appearance: 'new' as const },
	},
];

const styles = cssMap({
	carouselContainer: {
		display: 'flex',
		flexDirection: 'row',
		overflow: 'hidden',
		width: '100%',
		scrollSnapType: 'x mandatory',
		scrollBehavior: 'smooth',
		gap: token('space.150'),
		marginTop: token('space.250'),
	},
	slideWrapper: {
		flex: '0 0 100%',
		scrollSnapAlign: 'start',
		minWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.100'),
		textAlign: 'left',
	},
	textContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
		alignItems: 'flex-start',
		marginTop: token('space.200'),
	},
	imageStyles: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
		position: 'relative',
		backgroundColor: 'transparent',
		width: '100%',
		maxWidth: '800px',
		aspectRatio: '800 / 424',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		objectFit: 'cover',
	},
	lozengeOverlay: {
		position: 'absolute',
		top: token('space.200'),
		left: token('space.200'),
		zIndex: 1,
	},
	controlsContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
		marginTop: token('space.200'),
	},
});

const CarouselSlide = ({
	title,
	description,
	imageAlt,
	imageSrc,
	lozenge,
	index,
	selectedIndex,
}: {
	title: string;
	description: string;
	imageAlt: string;
	imageSrc: string;
	lozenge: { text: string; appearance: 'new' | 'inprogress' };
	index: number;
	selectedIndex: number;
}) => {
	const cardRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (cardRef.current && selectedIndex === index) {
			cardRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest',
			});
		}
	}, [selectedIndex, index]);

	return (
		<Box xcss={styles.slideWrapper} ref={cardRef}>
			<Box xcss={styles.imageStyles}>
				<Image src={imageSrc} alt={imageAlt} />
				<Box xcss={styles.lozengeOverlay}>
					<Lozenge appearance={lozenge.appearance} isBold maxWidth="None">
						{lozenge.text}
					</Lozenge>
				</Box>
			</Box>
			<Box xcss={styles.textContainer}>
				<Text size="large" weight="semibold">
					{title}
				</Text>
				<Text color="color.text.subtlest">{description}</Text>
			</Box>
		</Box>
	);
};

const CarouselModalContent = ({ onClose }: { onClose: () => void }) => {
	const { formatMessage } = useIntl();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	const carouselSlides = useMemo(() => getCarouselSlides(formatMessage), [formatMessage]);

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'RovoAwarenessCarouselModal',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePrevious = () => {
		const newIndex = selectedIndex === 0 ? carouselSlides.length - 1 : selectedIndex - 1;

		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'carouselPrevious',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessCarouselPreviousButton',
			attributes: {
				fromSlideIndex: selectedIndex,
				toSlideIndex: newIndex,
			},
		});

		setSelectedIndex(newIndex);
	};

	const handleNext = () => {
		const newIndex = selectedIndex === carouselSlides.length - 1 ? 0 : selectedIndex + 1;

		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'carouselNext',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessCarouselNextButton',
			attributes: {
				fromSlideIndex: selectedIndex,
				toSlideIndex: newIndex,
			},
		});

		setSelectedIndex(newIndex);
	};

	const handleSelectIndex = (index: number) => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'carouselProgressIndicator',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessCarouselProgressIndicator',
			attributes: {
				fromSlideIndex: selectedIndex,
				toSlideIndex: index,
			},
		});

		setSelectedIndex(index);
	};

	return (
		<Modal onClose={onClose} width="850px">
			{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
			<ModalHeader hasCloseButton>
				<Inline space="space.150" alignBlock="center">
					<RovoIcon size="small" appearance="brand" shouldUseNewLogoDesign />
					<Heading size="xlarge">
						<FormattedMessage {...i18n.carouselModalTitle} />
					</Heading>
				</Inline>
			</ModalHeader>
			<ModalBody>
				<Text color="color.text.subtlest">
					<FormattedMessage {...i18n.carouselModalDescription} />
				</Text>
				<Box xcss={styles.carouselContainer}>
					{carouselSlides.map((slide, index) => (
						<CarouselSlide
							key={index}
							title={slide.title}
							description={slide.description}
							imageAlt={slide.imageAlt}
							imageSrc={slide.imageSrc}
							lozenge={slide.lozenge}
							index={index}
							selectedIndex={selectedIndex}
						/>
					))}
				</Box>
			</ModalBody>
			<ModalFooter>
				<Box xcss={styles.controlsContainer}>
					<IconButton
						shape="circle"
						icon={(iconProps) => <ArrowLeftIcon {...iconProps} size="medium" />}
						label={formatMessage(i18n.carouselModalPreviousLabel)}
						onClick={handlePrevious}
					/>
					<ProgressIndicator
						onSelect={({ event: _, index }) => handleSelectIndex(index)}
						selectedIndex={selectedIndex}
						values={carouselSlides.map((_, i) => i)}
					/>
					<IconButton
						shape="circle"
						icon={(iconProps) => <ArrowRightIcon {...iconProps} size="medium" />}
						label={formatMessage(i18n.carouselModalNextLabel)}
						onClick={handleNext}
					/>
				</Box>
			</ModalFooter>
		</Modal>
	);
};

export const CarouselModal = ({ isOpen, onClose }: CarouselModalProps): JSX.Element => {
	return <ModalTransition>{isOpen && <CarouselModalContent onClose={onClose} />}</ModalTransition>;
};
